import type { ReactNode } from "react";
import "../../styles/components/avatar.css";

const GRADIENTS = [
  "linear-gradient(135deg, #5B5BF0, #7C3AED)",
  "linear-gradient(135deg, #4361EE, #4CC9F0)",
  "linear-gradient(135deg, #0A9D6F, #34D399)",
  "linear-gradient(135deg, #E08A00, #F59E0B)",
  "linear-gradient(135deg, #E5484D, #F97362)",
  "linear-gradient(135deg, #7C3AED, #EC4899)",
];

/** 由名称稳定派生一个渐变，保证同一个人头像配色始终一致 */
function gradientOf(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 9973;
  return GRADIENTS[hash % GRADIENTS.length];
}

export interface AvatarProps {
  name: string;
  size?: number;
  /** 有头像 URL 时优先展示图片，加载失败自动回退到首字母 */
  src?: string;
  title?: string;
}

export function Avatar({ name, size = 36, src, title }: AvatarProps) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4) };
  if (src) {
    return <img className="avatar" style={style} src={src} alt={name} title={title ?? name} />;
  }
  return (
    <span className="avatar" style={{ ...style, background: gradientOf(name) }} title={title ?? name} aria-label={name}>
      {initial}
    </span>
  );
}

export interface AvatarGroupProps {
  names: string[];
  size?: number;
  /** 最多展示几个，超出显示 +N */
  max?: number;
}

/** 头像组：审批链节点、协同成员等场景 */
export function AvatarGroup({ names, size = 28, max = 4 }: AvatarGroupProps) {
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return (
    <span className="avatar-group">
      {shown.map((name, i) => (
        <span className="avatar-group-item" key={`${name}-${i}`} style={{ zIndex: shown.length - i }}>
          <Avatar name={name} size={size} />
        </span>
      ))}
      {rest > 0 ? (
        <span className="avatar-group-rest" style={{ width: size, height: size, fontSize: size * 0.36 }}>
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

export interface BadgeProps {
  /** 数字角标；0 或未传且无 dot 时不渲染 */
  count?: number;
  max?: number;
  dot?: boolean;
  children: ReactNode;
}

/** 角标：通知铃铛、菜单项未读数 */
export function Badge({ count, max = 99, dot = false, children }: BadgeProps) {
  const show = dot || (typeof count === "number" && count > 0);
  return (
    <span className="badge-wrap">
      {children}
      {show ? (
        dot ? (
          <span className="badge-dot" />
        ) : (
          <span className="badge-count">{(count as number) > max ? `${max}+` : count}</span>
        )
      ) : null}
    </span>
  );
}

export default Avatar;
